// Catalogue maths for questionnaire groups (spark#196). Both values are derived,
// never stored: a duration field in the data model goes stale the moment somebody
// adds a question, and a stored tile colour is a second thing editors would have
// to maintain for no gain.

// Seconds a single question is assumed to take. Derivation: the Power Quiz's 90
// items were labelled "20-30 min" by hand, i.e. 13 to 20 seconds per item, so 15
// sits in the middle. This constant is the one place the estimate is tuned.
export const SECONDS_PER_QUESTION = 15;

// Estimates are reported in steps of this many minutes, so the tile never claims
// a precision the estimate does not have.
export const DURATION_STEP_MINUTES = 5;

// Rounds to the nearest DURATION_STEP_MINUTES, with the step itself as the floor:
// a short questionnaire should read "approx. 5 min", never "approx. 0 min".
// An empty group returns 0 - it renders no tile at all, so no duration is shown.
export function estimateDurationMinutes(itemCount: number): number {
  if (itemCount <= 0) return 0;
  const minutes = (itemCount * SECONDS_PER_QUESTION) / 60;
  const rounded = Math.round(minutes / DURATION_STEP_MINUTES) * DURATION_STEP_MINUTES;
  return Math.max(DURATION_STEP_MINUTES, rounded);
}

// Hue for the AbstractTile fallback shown when a group has no artwork. Derived
// from the group id so the same group keeps the same colour across loads, devices
// and users: a random hue would flicker on every render, a fixed one would make
// every unillustrated group look like the same tile.
export function tileHueFromId(questionnaireGroupId: string): number {
  let hash = 0;
  for (let i = 0; i < questionnaireGroupId.length; i++) {
    hash = (hash * 31 + questionnaireGroupId.charCodeAt(i)) % 360;
  }
  return hash;
}

export interface LocalizedTextLike {
  localized?: string | null;
  german?: string | null;
  english?: string | null;
}

// genesis resolves `localized` from the request's Accept-Language and does NOT
// fall back across languages: a group described only in English reads back as
// null under a German request. Editorial copy that exists in one language is
// still better than a blank line, so the other variants are the fallback.
export function localizedText(text: LocalizedTextLike | null | undefined): string {
  if (!text) return "";
  return text.localized || text.english || text.german || "";
}

// genesis `File` -> an <img src>. It inlines artwork as base64 rather than
// serving a URL, so there is nothing to fetch and nothing for the CSP to allow.
// Null when the group has no artwork; callers fall back to the AbstractTile.
export function groupImageSrc(
  image: { mimeType?: string | null; content?: string | null } | null | undefined,
): string | null {
  if (!image?.content) return null;
  return `data:${image.mimeType || "image/jpeg"};base64,${image.content}`;
}
